import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import {
  LIMITS,
  QUESTION_TYPES,
  TYPE_LABEL,
  blankQuestion,
  issueText,
  questionIssue,
  removeOption,
  trimQuestion,
  withType,
} from "./questionRules";

const inputCls = `w-full rounded-xl border border-terakkiBorder bg-terakkiSurface px-3 py-2 text-sm text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`;

const chipCls = (active) =>
  `px-3 py-1.5 rounded-full border text-sm transition-colors ${
    active
      ? "bg-terakkiPrimaryDark border-terakkiPrimaryDark text-white dark:bg-[#8FC9CA] dark:border-[#8FC9CA] dark:text-[#123B3D]"
      : `border-terakkiBorder dark:border-white/[0.14] ${cls.title} ${cls.rowHover}`
  }`;

/**
 * Soru düzenleyici (mobil QuestionEditorModal): tip seçilir, metin yazılır;
 * tipe göre şıklar ve doğru şık, Evet/Hayır ya da Doğru/Yanlış cevabı. Açık
 * uçlu soruda doğru cevap yok. Eksik, Kaydet'e basınca yazılır.
 * initial null ise yeni soru. Çekmece üstünde açıldığı için zIndex yüksek.
 */
export default function QuestionEditorDialog({ open, initial, onSave, onClose }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [draft, setDraft] = useState(() => initial || blankQuestion());
  const [touched, setTouched] = useState(false);

  // Her açılışta verilen soruyla (ya da boş soruyla) başlar.
  useEffect(() => {
    if (open) {
      setDraft(initial || blankQuestion());
      setTouched(false);
    }
  }, [open, initial]);

  const issue = questionIssue(draft);
  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const options = draft.options || [];

  const save = () => {
    setTouched(true);
    if (issue) return;
    onSave(trimQuestion(draft));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 3, ...paperSx } }}
    >
      <DialogTitle sx={{ fontWeight: 500 }}>{t(initial ? "mzq_edit_question" : "mzq_new_question")}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-1">
          <div className="flex flex-col gap-2">
            <span className={`text-xs ${cls.muted}`}>{t("mzq_type")}</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("mzq_type")}>
              {QUESTION_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={draft.type === type}
                  onClick={() => setDraft((d) => withType(d, type))}
                  className={chipCls(draft.type === type)}
                >
                  {t(TYPE_LABEL[type])}
                </button>
              ))}
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className={`flex justify-between text-xs ${cls.muted}`}>
              <span>{t("mzq_text")}</span>
              <span>{`${draft.text.length}/${LIMITS.questionTextMax}`}</span>
            </span>
            <textarea
              value={draft.text}
              onChange={(e) => update({ text: e.target.value })}
              maxLength={LIMITS.questionTextMax}
              rows={3}
              autoFocus
              className={inputCls}
            />
          </label>

          {draft.type === "coktan" ? (
            <div className="flex flex-col gap-2">
              <span className={`text-xs ${cls.muted}`}>{t("mzq_options_hint")}</span>
              <ul className="flex flex-col gap-2" role="radiogroup" aria-label={t("mzq_options")}>
                {options.map((option, i) => {
                  const correct = draft.correctIndex === i;
                  return (
                    <li key={i} className="flex items-center gap-2">
                      <IconButton
                        size="small"
                        role="radio"
                        aria-checked={correct}
                        aria-label={`${t("mzq_mark_correct")} ${i + 1}`}
                        onClick={() => update({ correctIndex: i })}
                      >
                        {correct ? (
                          <CheckCircleIcon fontSize="small" className="text-[#2E9E6A] dark:text-[#7BE3A8]" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" className={cls.muted} />
                        )}
                      </IconButton>
                      <input
                        value={option}
                        onChange={(e) =>
                          update({ options: options.map((o, j) => (j === i ? e.target.value : o)) })
                        }
                        maxLength={LIMITS.optionMax}
                        placeholder={`${t("mzq_option")} ${i + 1}`}
                        aria-label={`${t("mzq_option")} ${i + 1}`}
                        className={inputCls}
                      />
                      {options.length > LIMITS.optionsMin ? (
                        <IconButton
                          size="small"
                          aria-label={t("mzq_remove_option")}
                          onClick={() => setDraft((d) => removeOption(d, i))}
                        >
                          <CloseIcon fontSize="small" className={cls.muted} />
                        </IconButton>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {options.length < LIMITS.optionsMax ? (
                <button
                  type="button"
                  onClick={() => update({ options: [...options, ""] })}
                  className={`self-start flex items-center gap-1 text-sm ${cls.accent}`}
                >
                  <AddIcon fontSize="small" />
                  {t("mzq_add_option")}
                </button>
              ) : null}
            </div>
          ) : draft.type === "acik" ? (
            <p className={`text-sm ${cls.muted}`}>{t("mzq_open_hint")}</p>
          ) : (
            <div className="flex flex-col gap-2">
              <span className={`text-xs ${cls.muted}`}>{t("assess_correct_answer")}</span>
              <div className="flex gap-2.5">
                {(draft.type === "evethayir" ? ["assess_yes", "assess_no"] : ["assess_true", "assess_false"]).map(
                  (key, i) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={draft.correctBool === (i === 0)}
                      onClick={() => update({ correctBool: i === 0 })}
                      className={`flex-1 py-2 ${chipCls(draft.correctBool === (i === 0))}`}
                    >
                      {t(key)}
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {touched && issue ? (
            <p role="alert" className={`text-sm ${cls.danger}`}>
              {t(issueText(issue))}
            </p>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SoftButton tone="cancel" onClick={onClose}>
          {t("muzakere_cancel")}
        </SoftButton>
        <SoftButton tone="success" startIcon={<CheckIcon />} onClick={save} disabled={touched && !!issue}>
          {t("save")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
